import {
  ContentCreatorInsightsSupplementalData,
  ContentCreatorInsightsSupplementalDataValue,
  CreatorAttributeFilters,
  CreatorFilterState,
  CreatorInsightsCriteria,
  SearchAttributesAudienceMember,
  YouTubeChannelInsights,
  YouTubeCreatorInsights,
  YouTubeMetrics
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import { GetCreatorInsightsResponse } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  ACTIVE_SHORTS_BADGE_LABEL,
  AGE_RANGE_OPTIONS,
  CHANNEL_ID_PATTERN,
  CreatorRow,
  CreatorSearchDraft,
  CreatorSearchLabels,
  DEVICE_OPTIONS,
  EMPTY_DRAFT,
  ENTITY_ID_PATTERN,
  GENDER_OPTIONS,
  INCOME_RANGE_OPTIONS,
  INTEREST_ID_KEYS,
  InterestIdKey,
  PARENTAL_STATUS_OPTIONS,
  PARTNERSHIP_BADGE_LABELS,
  SearchMode,
  TOP_CREATORS_LIMIT,
  URL_PARAMS,
  USER_INTEREST_ID_PATTERN,
  ViewerDetails
} from './creator-discovery-constants';

const ATTRIBUTE_KEYS = [
  'ageRanges',
  'genders',
  'parentalStatuses',
  'incomeRanges',
  'devices',
  ...INTEREST_ID_KEYS
] as const satisfies readonly (keyof CreatorAttributeFilters)[];

// Drops empty lists, so the stored and URL-encoded state only names the filters in use.
const compactAttributes = (attributes: CreatorAttributeFilters): CreatorAttributeFilters => {
  const compact: CreatorAttributeFilters = {};
  const copy = <K extends keyof CreatorAttributeFilters>(key: K) => {
    const list = attributes[key];
    if (list?.length) {
      compact[key] = list;
    }
  };
  ATTRIBUTE_KEYS.forEach(copy);
  return compact;
};

export const toCreatorFilterState = (draft: CreatorSearchDraft): CreatorFilterState => {
  if (draft.mode === SearchMode.CHANNELS) return { channelIds: draft.channelIds };
  if (draft.mode === SearchMode.TOPICS) return { topicEntityIds: draft.topicEntityIds };
  return { attributes: compactAttributes(draft.attributes) };
};

export const fromCreatorFilterState = (state: CreatorFilterState): CreatorSearchDraft => {
  if (state.channelIds) {
    return { ...EMPTY_DRAFT, mode: SearchMode.CHANNELS, channelIds: state.channelIds };
  }
  if (state.topicEntityIds) {
    return { ...EMPTY_DRAFT, mode: SearchMode.TOPICS, topicEntityIds: state.topicEntityIds };
  }
  return { ...EMPTY_DRAFT, attributes: state.attributes ?? {} };
};

// Mirrors getCreatorInsights' 400: the active mode needs at least one value. The lambda's
// audienceCombinations exclusion can't arise, since CreatorFilterState has no combinations.
export const isSearchable = (state: CreatorFilterState): boolean => {
  if (state.channelIds) return state.channelIds.length > 0;
  if (state.topicEntityIds) return state.topicEntityIds.length > 0;
  return ATTRIBUTE_KEYS.some(key => Boolean(state.attributes?.[key]?.length));
};

// The typeahead returns a user interest's bare id; Google wants the resource name under this
// account, built the same way createPlan builds it.
export const toUserInterestResourceName = (googleAdsAccount: string, id: string): string =>
  `customers/${googleAdsAccount}/userInterests/${id}`;

const toAudienceAttributes = (
  attributes: CreatorAttributeFilters,
  googleAdsAccount: string
): SearchAttributesAudienceMember[] => [
  ...(attributes.ageRanges ?? []).map(type => ({ ageRange: { type } })),
  ...(attributes.genders ?? []).map(type => ({ gender: { type } })),
  ...(attributes.parentalStatuses ?? []).map(type => ({ parentalStatus: { type } })),
  ...(attributes.incomeRanges ?? []).map(type => ({ incomeRange: { type } })),
  ...(attributes.devices ?? []).map(type => ({ device: { type } })),
  ...(attributes.userInterestIds ?? []).map(id => ({
    userInterest: { userInterestCategory: toUserInterestResourceName(googleAdsAccount, id) }
  })),
  ...(attributes.entityIds ?? []).map(knowledgeGraphMachineId => ({
    entity: { knowledgeGraphMachineId }
  })),
  ...(attributes.categoryIds ?? []).map(categoryId => ({ category: { categoryId } }))
];

// The flat filter state as the one criteria branch getCreatorInsights forwards to Google.
export const buildCreatorInsightsCriteria = (
  state: CreatorFilterState,
  googleAdsAccount: string
): CreatorInsightsCriteria => {
  if (state.channelIds) {
    return {
      searchChannels: { youtubeChannels: state.channelIds.map(channelId => ({ channelId })) }
    };
  }
  if (state.topicEntityIds) {
    return {
      searchTopics: {
        entities: state.topicEntityIds.map(knowledgeGraphMachineId => ({ knowledgeGraphMachineId }))
      }
    };
  }
  return {
    searchAttributes: {
      audienceAttributes: toAudienceAttributes(state.attributes ?? {}, googleAdsAccount)
    }
  };
};

export const getInterestIds = (attributes: CreatorAttributeFilters): string[] =>
  INTEREST_ID_KEYS.flatMap(key => attributes[key] ?? []);

// Applies the interest typeahead's new selection (one flat list) to the three id lists. An id
// already selected stays in its list; a new one goes where `keyOf` (its typeahead dimension) says,
// and is dropped if its dimension is unknown.
export const withInterestSelection = (
  attributes: CreatorAttributeFilters,
  values: string[],
  keyOf: (value: string) => InterestIdKey | undefined
): CreatorAttributeFilters => {
  const currentKey = (id: string) => INTEREST_ID_KEYS.find(key => attributes[key]?.includes(id));
  const next = { ...attributes };
  INTEREST_ID_KEYS.forEach(key => {
    next[key] = values.filter(value => (currentKey(value) ?? keyOf(value)) === key);
  });
  return next;
};

// Dropdown options for selected ids, labelled from `labels` and falling back to the id itself.
export const toLabelledOptions = (ids: string[], labels: CreatorSearchLabels): MenuItem<string>[] =>
  ids.map(id => ({ value: id, label: labels[id] ?? id }));

// Channel ids from pasted text: any mix of commas and whitespace, invalid ids dropped.
export const parseChannelIds = (input: string): string[] => [
  ...new Set(input.split(/[\s,]+/).filter(id => CHANNEL_ID_PATTERN.test(id)))
];

const getLabelledIds = (state: CreatorFilterState): string[] => {
  if (state.channelIds) return state.channelIds;
  if (state.topicEntityIds) return state.topicEntityIds;
  return getInterestIds(state.attributes ?? {});
};

export const searchToParams = (
  state: CreatorFilterState,
  labels: CreatorSearchLabels
): Record<string, string> => {
  const usedLabels = Object.fromEntries(
    getLabelledIds(state)
      .filter(id => labels[id] !== undefined)
      .map(id => [id, labels[id]])
  );
  return {
    [URL_PARAMS.SEARCH]: JSON.stringify(state),
    [URL_PARAMS.LABELS]: JSON.stringify(usedLabels)
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

const toStringArray = (value: unknown): string[] => (isStringArray(value) ? value : []);

// Keeps only the ids matching `pattern`, the same checks the interactive inputs apply, so a
// hand-edited URL can't spend a billed search on ids Google will reject.
const toMatchingIds = (value: unknown, pattern: RegExp): string[] =>
  toStringArray(value).filter(id => pattern.test(id));

const parseJSON = (text: string | null): unknown => {
  if (text === null) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

// Keeps only the values an enum-typed filter accepts, since a hand-edited URL would otherwise send
// Google an unknown enum and get a 400.
const pickAllowed = <T extends string>(value: unknown, options: { value: T }[]): T[] => {
  if (!isStringArray(value)) return [];
  return options.map(option => option.value).filter(allowed => value.includes(allowed));
};

const parseAttributes = (value: Record<string, unknown>): CreatorAttributeFilters =>
  compactAttributes({
    ageRanges: pickAllowed(value.ageRanges, AGE_RANGE_OPTIONS),
    genders: pickAllowed(value.genders, GENDER_OPTIONS),
    parentalStatuses: pickAllowed(value.parentalStatuses, PARENTAL_STATUS_OPTIONS),
    incomeRanges: pickAllowed(value.incomeRanges, INCOME_RANGE_OPTIONS),
    devices: pickAllowed(value.devices, DEVICE_OPTIONS),
    userInterestIds: toMatchingIds(value.userInterestIds, USER_INTEREST_ID_PATTERN),
    entityIds: toMatchingIds(value.entityIds, ENTITY_ID_PATTERN),
    categoryIds: toStringArray(value.categoryIds)
  });

// URL input is untrusted: anything that isn't exactly one well-formed mode reads as no search.
export const parseCreatorFilterState = (value: unknown): CreatorFilterState | undefined => {
  if (!isRecord(value) || Object.keys(value).length !== 1) return undefined;
  const { attributes, topicEntityIds, channelIds } = value;
  if (isStringArray(channelIds)) {
    return { channelIds: toMatchingIds(channelIds, CHANNEL_ID_PATTERN) };
  }
  if (isStringArray(topicEntityIds)) {
    return { topicEntityIds: toMatchingIds(topicEntityIds, ENTITY_ID_PATTERN) };
  }
  if (isRecord(attributes)) return { attributes: parseAttributes(attributes) };
  return undefined;
};

export const paramsToSearch = (
  params: URLSearchParams
): { search?: CreatorFilterState; labels: CreatorSearchLabels } => {
  const labels = parseJSON(params.get(URL_PARAMS.LABELS));
  return {
    search: parseCreatorFilterState(parseJSON(params.get(URL_PARAMS.SEARCH))),
    labels: isRecord(labels)
      ? Object.fromEntries(
          Object.entries(labels).filter(
            (entry): entry is [string, string] => typeof entry[1] === 'string'
          )
        )
      : {}
  };
};

// Only topic searches ask for Google's local-creator list.
export const getSupplementalData = (
  state: CreatorFilterState
): ContentCreatorInsightsSupplementalDataValue[] | undefined =>
  state.topicEntityIds ? [ContentCreatorInsightsSupplementalData.LOCAL_CREATOR_DATA] : undefined;

// An Int64String count; proto3 drops zeros, so an absent count is 0.
const toCount = (count?: string): number => {
  const parsed = Number(count ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getCreatorBadges = ({
  partnershipOpportunities = [],
  isActiveShortsCreator
}: YouTubeMetrics): string[] => [
  ...partnershipOpportunities.flatMap(opportunity => {
    const label = PARTNERSHIP_BADGE_LABELS[opportunity];
    return label ? [label] : [];
  }),
  ...(isActiveShortsCreator ? [ACTIVE_SHORTS_BADGE_LABEL] : [])
];

const toCreatorRow = (channel: YouTubeChannelInsights, isLocal: boolean): CreatorRow => {
  const metrics = channel.channelMetrics ?? {};
  const sharesViewerData = Boolean(channel.dataSharingConsentGiven);
  // Google only sends these for channels that share viewer data; for those, absent means 0.
  const gated = (value?: number): number | undefined =>
    sharesViewerData ? (value ?? 0) : undefined;
  return {
    channelId: channel.youtubeChannel.channelId,
    displayName: channel.displayName,
    handle: channel.handle,
    channelUrl: channel.channelUrl,
    thumbnailUrl: channel.thumbnailUrl,
    subscribers: toCount(metrics.subscriberCount),
    views: toCount(metrics.viewsCount),
    videos: toCount(metrics.videoCount),
    averageViews: gated(metrics.averageViewsPerVideo),
    engagementRate: gated(metrics.engagementRate),
    relevance: channel.relevanceScore,
    viewerDetails: sharesViewerData ? ViewerDetails.SHARED : ViewerDetails.NOT_SHARED,
    badges: getCreatorBadges(metrics),
    isLocal,
    channel
  };
};

const getChannels = (creators: YouTubeCreatorInsights[]): YouTubeChannelInsights[] =>
  creators.flatMap(creator => creator.creatorChannels ?? []);

// One row per channel, in Google's relevance order: the main list, then local creators it didn't
// already include. A channel in both lists is marked local.
export const toCreatorRows = ({
  creatorInsights = [],
  localCreatorInsights = []
}: GetCreatorInsightsResponse): CreatorRow[] => {
  const localChannels = getChannels(localCreatorInsights);
  const localIds = new Set(localChannels.map(channel => channel.youtubeChannel.channelId));
  const rows = new Map<string, CreatorRow>();
  [...getChannels(creatorInsights), ...localChannels].forEach(channel => {
    const { channelId } = channel.youtubeChannel;
    if (!rows.has(channelId)) {
      rows.set(channelId, toCreatorRow(channel, localIds.has(channelId)));
    }
  });
  return [...rows.values()];
};

const COMPACT_NUMBER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1
});

// "1.3M"; "—" for a metric the creator doesn't share.
export const formatCompactCount = (count?: number): string =>
  count === undefined ? '—' : COMPACT_NUMBER.format(count);

// A 0–1 share as "4.2%"; "—" for a metric the creator doesn't share.
export const formatShare = (share?: number): string =>
  share === undefined ? '—' : `${(share * 100).toFixed(1)}%`;

const describeCreatorCount = (
  mainCreators: number,
  mainCount: number,
  extraLocal: number
): string => {
  if (mainCount === 0) return conditionallyPluralizeWithCount(extraLocal, 'local creator');
  const count =
    mainCreators >= TOP_CREATORS_LIMIT
      ? `Showing the top ${TOP_CREATORS_LIMIT} creators`
      : conditionallyPluralizeWithCount(mainCount, 'creator');
  return extraLocal > 0 ? `${count}, plus ${extraLocal} local` : count;
};

// The line above the results, e.g. "Showing the top 100 creators, plus 39 local · 12 don't share
// viewer details". Google caps the main list at 100, so a full list says so.
export const describeCreatorResults = (
  response: GetCreatorInsightsResponse,
  rows: CreatorRow[]
): string => {
  const mainCreators = response.creatorInsights ?? [];
  // Rows are channels, and a creator can own several.
  const mainCount = new Set(
    getChannels(mainCreators).map(channel => channel.youtubeChannel.channelId)
  ).size;
  const extraLocal = rows.length - mainCount;
  const notSharing = rows.filter(row => row.viewerDetails === ViewerDetails.NOT_SHARED).length;
  return [
    describeCreatorCount(mainCreators.length, mainCount, extraLocal),
    ...(notSharing > 0
      ? [`${notSharing} ${notSharing === 1 ? "doesn't" : "don't"} share viewer details`]
      : [])
  ].join(' · ');
};
